import type { CSSProperties, ReactNode, SVGProps } from 'react';

import type { headerMenuProps } from './types';

// TODO: pixel-react has no Test development / Integration icons yet (FF3-19668), so the More menu showed none.
// These are the Figma icons; move them into pixel-react's icon list when it adds them.
const svgProps: SVGProps<SVGSVGElement> = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  // Inherit the menu item's text colour so the icon stays visible on the purple menu and on hover.
  'aria-hidden': true,
};

// Same box as pixel-react's <Icon> (16px plus 4px padding) so the labels line up with the other menu items
const boxStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'content-box',
  width: 16,
  height: 16,
  padding: 4,
  flexShrink: 0,
};

// The More menu fills every `svg path`; these are outline icons, so keep their paths unfilled
const outline: CSSProperties = { fill: 'none' };

const TestDevIcon = (): ReactNode => (
  <span style={boxStyle}>
    <svg {...svgProps}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path style={outline} d="M3 7.5h18" />
      <circle cx="6" cy="5.25" r="0.5" fill="currentColor" />
      <circle cx="8" cy="5.25" r="0.5" fill="currentColor" />
      <circle cx="10" cy="5.25" r="0.5" fill="currentColor" />
      <path
        style={outline}
        d="M9 11.5l-2.25 2.25L9 16M15 11.5l2.25 2.25L15 16M13 10.75l-2 6"
      />
    </svg>
  </span>
);

const IntegrationIcon = (): ReactNode => (
  <span style={boxStyle}>
    <svg {...svgProps}>
      <rect x="6.5" y="6.5" width="11" height="11" rx="2" />
      <path
        style={outline}
        d="M10 3v3.5M14 3v3.5M10 17.5V21M14 17.5V21M3 10h3.5M3 14h3.5M17.5 10H21M17.5 14H21"
      />
      <path
        style={outline}
        d="M11 10.25L9.5 12l1.5 1.75M13 10.25l1.5 1.75-1.5 1.75"
      />
    </svg>
  </span>
);

const RepoIcon = (): ReactNode => (
  <span style={boxStyle}>
    <svg {...svgProps}>
      <circle style={outline} cx="7" cy="5" r="2" />
      <circle style={outline} cx="7" cy="19" r="2" />
      <circle style={outline} cx="17" cy="8" r="2" />
      <path style={outline} d="M7 7v10M17 10c0 3-2.5 4-5 4.5S7 15.5 7 17" />
    </svg>
  </span>
);

const MENU_ICONS: Record<string, ReactNode> = {
  'Test Dev': <TestDevIcon />,
  Integrations: <IntegrationIcon />,
  Repo: <RepoIcon />,
  Repository: <RepoIcon />,
};

/**
 * Gives Test Dev, Integrations and Repo their icons in the header's More menu.
 */
export const withHeaderMenuIcons = (
  headers: headerMenuProps[],
): headerMenuProps[] =>
  headers.map(header =>
    MENU_ICONS[header.label]
      ? { ...header, iconName: MENU_ICONS[header.label] }
      : header,
  );

const PROJECT_TYPE_ICONS: Record<string, string> = {
  Web: 'web_icon',
  Mobile: 'mobile_icon',
  'Multi-Channel': 'multichannel',
  'MS Dynamics': 'ms_dynamic',
  Salesforce: 'sales_force',
  'Web Service': 'web_service_icon',
  Desktop: 'local',
};

/** The project selector's icon for a project type; Web for unknown types */
export const getProjectTypeIconNames = (type: string): string =>
  PROJECT_TYPE_ICONS[type] ?? 'web_icon';
